import React, { useEffect, useState } from "react";

import { useSnackbar } from "notistack";

import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  makeStyles,
  Typography,
  Tooltip,
} from "@material-ui/core";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import { withStyles } from "@material-ui/core/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Brightness3Icon from "@material-ui/icons/Brightness3";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import { getAmountOut, getBalanceAndSymbol } from "../ethereumFunctions";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    fontSize: "1rem",
    background: theme.palette.background.paper,
    height: "39px",
    borderRadius: "14px",
    padding: "0px 15px",
    textTransform: "initial",
  },
  buttonIconBox: {
    background: theme.palette.background.default,
    width: "32px",
    height: "32px",
    borderRadius: "12px",
    cursor: "pointer",
  },
  paddingBox: {
    padding: "0px 8px",
  },
  address: {
    fontSize: "16px",
    fontWeight: 600,
  },
  hr: {
    marginTop: 0,
    color: theme.palette.grey[400],
  },
  buttonIconSVG: {
    fontSize: "14px",
  },
  coinImage: {
    width: "20px",
    marginRight: theme.spacing(0.5),
  },
  balanceBox: {
    padding: "16px 0px",
    textAlign: "center",
  },
  meunItem: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    "&:hover": {
      backgroundColor: theme.palette.background.default,
    },
  },
  coinName: {
    fontWeight: 500,
  },
}));

const StyledMenu = withStyles({
  paper: {
    borderRadius: "20px",
    marginTop: "10px",
    width: "272px",
    padding: "0px 8px",
  },
})((props) => (
  <Menu
    elevation={2}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
));

function WalletDropdown({ network, darkMode, onChange }) {
  const { enqueueSnackbar } = useSnackbar();

  const [wallet, setWallet] = useState("");
  const [balanceInfo, setBalanceInfo] = useState({
    balance: 0,
    symbol: "",
  });

  const [maticUsdt, setMaticUsdt] = useState("0.00");

  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // shorten the checksummed version of the input address to have 0x + 4 characters at start and end
  const shortenAddress = (address, startChars = 4, endChars = 4) => {
    if (typeof address === "object") {
      return "";
    }
    return `${address.substring(0, startChars)}...${address.substring(
      42 - endChars
    )}`;
  };

  const investLink = (wallet) => {
    const { origin } = window.location;

    return `${origin}/invest?referral=${wallet}`;
  };

  /**
   * Copy To Clipboard
   */
  const copy = (text) => {
    navigator.clipboard.writeText(text);

    enqueueSnackbar("Copy to clipboard", {
      variant: "success",
      autoHideDuration: 1000,
    });
  };

  const calc_matic_usdt_balance = (maticBalance) => {
    let address = "";
    for (let i = 0; i < network.coins.length; i++) {
      const coin = network.coins[i];
      if (coin.abbr === "USDT") {
        address = coin.address;
        break;
      }
    }

    getAmountOut(
      network.coins[0].address,
      address,
      `${maticBalance}`,
      network.router
    ).then((amount) => {
      if (amount) {
        setMaticUsdt(amount.toFixed(2));
      } else {
        setMaticUsdt("0.00");
      }
    });
  };

  useEffect(() => {
    const walletTimeout = setTimeout(() => {
      if (typeof network.account !== "object") {
        getBalanceAndSymbol(
          network.account,
          network.coins[0].address,
          network.wethAddress,
          network.coins
        ).then((data) => {
          let info = { ...balanceInfo };
          info.balance = data.balance;
          info.symbol = data.symbol;
          setBalanceInfo(info);

          calc_matic_usdt_balance(data.balance);
        });
      }
      setWallet(network.account);
    }, 2000);

    return () => clearTimeout(walletTimeout);
  });

  return (
    <div>
      <Button
        aria-label="delete"
        className={classes.iconButton}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        {shortenAddress(wallet, 6)}
        <ExpandMoreIcon />
      </Button>

      <StyledMenu
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        disablePortal
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <div className={classes.paddingBox}>
          <div className="d-flex align-items-center justify-content-between">
            <Typography variant="body1" className={classes.address}>
              {shortenAddress(wallet, 4)}
            </Typography>

            <div>
              <IconButton
                onClick={() => copy(wallet)}
                variant="contained"
                className={classes.buttonIconBox}
              >
                <FileCopyIcon className={classes.buttonIconSVG} />
              </IconButton>
            </div>
          </div>
          <div className={classes.balanceBox}>
            <Typography variant="h4" className="mb-2">
              {Number(balanceInfo.balance)
                .toFixed(3)
                .replace(/\.?0+$/, "")}{" "}
              {balanceInfo.symbol}
            </Typography>

            <Typography variant="body1">$ {maticUsdt} USD</Typography>
          </div>
        </div>

        <hr className={classes.hr} />

        <MenuItem classes={{ root: classes.meunItem }}>
          Transactions
          <ChevronRightIcon />
        </MenuItem>
        <MenuItem classes={{ root: classes.meunItem }}>
          Language
          <ChevronRightIcon />
        </MenuItem>

        <MenuItem classes={{ root: classes.meunItem }} onClick={onChange}>
          Dark Theme : {darkMode ? <WbSunnyIcon /> : <Brightness3Icon />}
        </MenuItem>

        <MenuItem
          onClick={() => copy(investLink(wallet))}
          classes={{ root: classes.meunItem }}
        >
          Invest Referral Link
          <FileCopyIcon />
        </MenuItem>
      </StyledMenu>
    </div>
  );
}

export default WalletDropdown;
